import { lazy, Suspense } from 'react'
import type { Location } from 'react-router-dom'
import { Route, Routes, useLocation } from 'react-router-dom'

import { Shell } from './Shell'
import { Calendar } from './routes/Calendar'
import { Highlights } from './routes/Highlights'
import { Claim } from './routes/Claim'
import { Export } from './routes/Export'
import { Invite } from './routes/Invite'
import { Invited } from './routes/Invited'
import { Join } from './routes/Join'
import { Login } from './routes/Login'
import { Genre } from './routes/Genre'
import { Tidy } from './routes/Tidy'
import { Studio } from './routes/Studio'
import { Venue } from './routes/Venue'
import { Overlay } from './sheet/Overlay'
import { Profile } from './routes/Profile'
import { Artist } from './routes/Artist'
import { Results } from './routes/Results'
import { You } from './routes/You'
import { SharePage } from './share/SharePage'

/**
 * The routes, which are the ones the site already answers to. They are not new addresses:
 * `/e/:id`, `/v/:slug` and `/a/:id` have been real, shareable pages since the routing was
 * rebuilt, and 135 links were repaired to make them so.
 *
 * `/e/` and `/a/` are drawn twice over: once as a page, and once as a sheet over whatever
 * you were reading. Which one you get depends on how you arrived, and that is the point.
 * Pressing a card on the front page used to take you to the calendar — a page you had not
 * asked for, in a view you had not chosen, with the highlights you were reading gone.
 *
 * The mechanism is a location in `state`. A link pressed inside the app carries the page it
 * was pressed on, the routes below are rendered against *that*, and the sheet is rendered
 * beside them. A link opened cold has no such state, so the page underneath is the front
 * door — which is what a cold visit gets everywhere else here.
 */
interface Background {
  background?: Location
}

/*
 * The addresses drawn *over* whatever you were reading. A gig is one: it is a detail of the
 * page you pressed it on, and taking that page away to show it loses your place.
 *
 * `/brief` is not, and used to be. `/briefing` is still the endpoint the page fetches from —
 * the two names have to differ, because a route cannot be both the document and its own API.
 */
const OVER = ['/e/']

/*
 * The latency dashboard, fetched only when somebody opens it.
 *
 * Every other route is in the one bundle every visitor downloads on their first page. This
 * one is for the editor alone, so its code is a chunk of its own: a signed-out first visit to
 * the front page never asks for it. Until the chunk arrives the page is what it is until its
 * numbers do, a busy `<main>` with nothing in it.
 */
const Latency = lazy(() => import('./routes/Latency'))

export function App() {
  const location = useLocation()
  const state = location.state as Background | null
  const overlaid = OVER.some(prefix => location.pathname.startsWith(prefix))
  // A cold visit to `/e/<id>` has nothing underneath it, so it gets the front page.
  const beneath = overlaid ? (state?.background ?? { ...location, pathname: '/' }) : location

  return (
    <>
      <Routes location={beneath}>
        {/* Every view is a child of the shell, so the masthead is mounted once and never by
            a route. A view that drew its own would be the two-documents problem again, one
            component deeper. */}
        <Route element={<Shell />}>
          <Route path="/" element={<Highlights />} />
          <Route path="/calendar" element={<Calendar />} />
          {/* Three names for your own page; `You` is the signpost that folds them into
              one. See its own note for what each of them used to do instead. */}
          <Route path="/you" element={<You />} />
          <Route path="/profile" element={<You />} />
          <Route path="/search" element={<Results />} />
          {/* The acts that are not acts. Editor-only, and the page says so rather
              than the route hiding — a typed address should answer. */}
          <Route path="/tidy" element={<Tidy />} />
          {/* Where the Instagram cards are made. Editor-only on the same terms as `/tidy`:
              the route answers everybody and the page says whose it is. */}
          <Route path="/studio" element={<Studio />} />
          {/* The editor's dashboard, editor-only on `/studio`'s terms. */}
          <Route
            path="/latency"
            element={
              <Suspense fallback={<main aria-busy="true" />}>
                <Latency />
              </Suspense>
            }
          />
          <Route path="/u/:username" element={<Profile />} />
          <Route path="/v/:slug" element={<Venue />} />
          {/* A genre is a place, not a state of the calendar — see `Genre.tsx`. The
              parameter is the tag itself, spaces and all, because MusicBrainz's
              vocabulary is what matches and slugging it would need a reverse map. */}
          <Route path="/g/:genre" element={<Genre />} />
          <Route path="/a/:id" element={<Artist />} />
          <Route path="/login" element={<Login />} />
          <Route path="/join" element={<Join />} />
          <Route path="/claim/:token" element={<Claim />} />
          <Route path="/i/:token" element={<Invited />} />
          <Route path="/invite" element={<Invite />} />
          <Route path="/export" element={<Export />} />
          {/*
            * A page, not an overlay, which is what its own stylesheet has said all along:
            * "It is a screen and not a dialog — a modal made a decision queue feel like an
            * interruption to get past; as a page it is somewhere you go."
            *
            * It was in `OVER`, and an overlay here is a sibling rendered *after* the whole
            * page rather than over it — nothing in the briefing positions itself, so it laid
            * out below the entire calendar, off the bottom of the window. Pressing Briefing
            * changed the address, rendered the queue, and moved nothing on screen. Reported
            * three times as a button that does nothing, and it was: the thing it opened was
            * a scroll away, under everything.
            */}
          <Route path="/brief" element={<Overlay kind="briefing" />} />
          {/* Only reached when the address is one of these and nothing is overlaid, which
              cannot happen — but a route table with a hole in it is worse than one with a
              line that never runs. */}
          <Route path="/e/:id" element={<Highlights />} />
        </Route>
        {/* The one address outside the shell: the card headless Chrome photographs. A
            masthead over a 1080×1350 frame would be in the picture, so the card is the
            whole document, with no `MarksProvider` and nothing to press. Nothing on the
            site links here; the operator has the address. */}
        <Route path="/share-page/*" element={<SharePage />} />
      </Routes>

      {overlaid ? (
        <Routes>
          <Route path="/e/:id" element={<Overlay kind="event" />} />
        </Routes>
      ) : null}
    </>
  )
}
